import { toast } from 'react-hot-toast';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Cookies from 'js-cookie';
import type { Address } from '@/lib/types/customer/Address';
import { emailSchema } from '../validations/sharedValidations';
import { storage_keys } from '@/constant/configs';
import { i18n } from '@/i18n-config';
import { ActiveSubscription } from '../types/customer/restaurant/ActiveSubscription';
import { LastSubscription } from '../types/restaurant/User';
require('dayjs/locale/de');

dayjs.extend(relativeTime);

export const getTimeFromNow = (date: Date | string) => {
  return dayjs(date).fromNow();
};

interface FormatNumberOptions {
  value: number | string;
  locales?: string;
  style?: 'decimal' | 'currency' | 'percent';
  currency?: string;
  currencyDisplay?: 'symbol' | 'code' | 'name';
}

export const formatNumber = ({
  value,
  locales = 'de-DE',
  style = 'currency',
  currency = 'EUR',
  currencyDisplay = 'symbol',
}: FormatNumberOptions): string | any => {
  const options: Intl.NumberFormatOptions = { style, currency };

  if (currencyDisplay) {
    options.currencyDisplay = currencyDisplay;
  }

  try {
    if (!['string', 'number'].includes(typeof value)) {
      throw new Error('Value must be a number.');
    }

    if (typeof value === 'string') {
      value = parseFloat(parseFloat(value).toFixed(2));
    }

    return new Intl.NumberFormat(locales, options).format(value);
  } catch (error) {
    return value; // Return the original value on error
  }
};

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export const serializeParam = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  for (const key in params) {
    if (params[key] !== undefined) {
      if (Array.isArray(params[key])) {
        params[key].forEach((value: string) => searchParams.append(key, value));
      } else {
        searchParams.append(key, params[key]);
      }
    }
  }

  return searchParams.toString();
};

export const toastErrors = (erorrs: any) => {
  if (erorrs?.errors) {
    const errors = erorrs.errors;
    Object.keys(errors)?.forEach((key: string) => {
      toast.error(errors?.[key]?.[0], { position: 'top-center' });
    });
  } else if (erorrs?.message) {
    toast.error(erorrs?.message, { position: 'top-center' });
  } else {
    toast.error('Something went wrong!', { position: 'top-center' });
  }
};

export const setHookFormErrors = (
  errorsData: any,
  setFormError?: (
    key: any,
    { type, message }: { type: string; message: string }
  ) => void
) => {
  if (errorsData) {
    Object.keys(errorsData).forEach((key) => {
      setFormError?.(key, {
        type: 'server',
        message: errorsData[key]?.join('. '),
      });
    });
  }
};

type HandleErrorsArgsType = {
  errors: any;
  type?: 'form' | 'toast' | any;
  setError?: (
    key: any,
    { type, message }: { type: string; message: string }
  ) => void;
};

export const handleErrors = ({
  type = 'form',
  errors,
  setError,
}: HandleErrorsArgsType) => {
  const respStatus = errors?.status;
  if (respStatus === 503) {
    toast.error(
      errors?.data?.message ||
        errors?.statusText ||
        '503 Service Unavailable - Scheduled Maintenance, Please Check Back Shortly.'
    );
  }
  if (respStatus === 500) {
    toast.error(
      errors?.data?.message || errors?.statusText || '500 server error!'
    );
  } else if (respStatus === 403) {
    toast.error(
      errors?.data?.message || errors?.statusText || 'Unauthorize access!'
    );
  } else if (respStatus === 401) {
    toast.error(
      errors?.data?.message || errors?.statusText || 'Unauthenticated!'
    );
  } else if (type === 'form') {
    setHookFormErrors(errors?.data?.errors, setError);
  } else {
    toastErrors(errors?.data);
  }
};

dayjs.extend(localizedFormat);

export const dayFormat = dayjs;
export const defaultLocale = Cookies.get('4TA_LOCALE') ?? 'de-DE';

export const formatDateForDeliverySchedule = (date: Date) => {
  // Format dates for placing order
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatTime = (time: string): string => {
  return dayjs(time, 'H:mm:ssZ').format('HH:mm');
};

export const convertMoneyToEuropeanLocale = (
  locale = 'de',
  input: string | number
): string => {
  if (typeof input === 'number') {
    return input.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else if (typeof input === 'string') {
    const numberValue = parseFloat(input);
    if (!isNaN(numberValue)) {
      return numberValue.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }
  return 'Invalid input';
};

export const showAddress = (address: Address) => {
  return `${address.street_name} ${address.house_number}, ${address.zip_code} ${address.city}`;
};

export function htmlToPlainTextWithClosingTagSpacing(html: string): string {
  const tempElement = document && document.createElement('div');
  tempElement.innerHTML = html;

  function traverseAndConvert(node: Node): string {
    let result = '';
    if (node.nodeType === Node.TEXT_NODE) {
      result = node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.tagName) {
        // Add a space for the closing tag
        result += ` `;
      }
      for (let i = 0; i < element.childNodes.length; i++) {
        result += traverseAndConvert(element.childNodes[i]);
      }
    }
    return result;
  }

  return traverseAndConvert(tempElement);
}

export function htmlToPlainTextParser(html: string, maxLength = 50) {
  let htmlString = '';
  if (html?.length > 0) {
    htmlString = html;
  }
  // Remove HTML tags
  const cleanedDescription = htmlString
    .replace(/<[^>]*>/g, ' ')
    // Replace &nbsp; with a space
    .replace(/&nbsp;/g, ' ')
    // Handle other common HTML entities if needed (optional)
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

  // Trim to maxLength if necessary
  return cleanedDescription.length > maxLength
    ? cleanedDescription.slice(0, maxLength) + '...'
    : cleanedDescription;
}

export const countryCodeExistsInPhone = (phone: string): string => {
  if (phone.includes('+49')) return 'de';
  if (phone.includes('+41')) return 'ch';
  if (phone.includes('+43')) return 'at';
  return '';
};

export const isActivePlan = (plan: LastSubscription): boolean => {
  return plan.status === 'active' || plan.status === 'in_transition';
};

export function validateEmail(email: string): boolean {
  try {
    emailSchema.parse(email);
    return true;
  } catch (error) {
    return false;
  }
}

export const getValidNativeDateFormat = (value: string) => {
  // Split the string into an array ["08", "11", "2023"] to make valid date format YYYY-MM-DD
  const dateParts: any[] = value?.length > 1 ? value.split('-') : [];
  const formattedDate: any =
    dateParts?.length >= 3
      ? new Date(dateParts[2], parseInt(dateParts[1]) - 1, dateParts[0])
      : null;

  return formattedDate;
};

export const removeHTMLTags = (description: string, maxLength = 50) => {
  if (!description) return '';
  const cleanedDescription = description
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[^\s;]+;/g, ' ');
  return cleanedDescription.length > maxLength
    ? cleanedDescription.slice(0, maxLength) + '...'
    : cleanedDescription;
};

export function objectToQueryString(obj: any) {
  const qString = Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&');

  return `?` + qString;
}

export function isObject(obj: any) {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

export function isValidObject(obj: any) {
  return isObject(obj) && Object.keys(obj).length > 0;
}

export function getQueryParams(queryString: string) {
  const params: any = {};
  const queryStringWithoutQuestionMark = queryString.split('?')[1];

  if (queryStringWithoutQuestionMark) {
    const keyValuePairs = queryStringWithoutQuestionMark.split('&');

    keyValuePairs.forEach((keyValue) => {
      const [key, value] = keyValue.split('=');
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });
  }

  return params;
}

export function isPaidSubscriber(subscription: ActiveSubscription | Falsy) {
  if (!subscription) return false;
  if (subscription.free_of_charge) return false;
  return true;
}

export function isBasicSubscriber(subscription: ActiveSubscription | Falsy) {
  if (!subscription) return true;
  if (subscription.free_of_charge) return true;
  return false;
}

export async function getLocaleInServer() {
  const { cookies } = await import('next/headers');
  return cookies().get(storage_keys.locale)?.value || i18n.defaultLocale;
}
export async function getLangInServer() {
  const { cookies } = await import('next/headers');
  return (
    cookies().get(storage_keys.locale)?.value.slice(0, 2) ||
    i18n.defaultLanguage
  );
}

export function getLocaleInClient() {
  return Cookies.get(storage_keys.locale) || i18n.defaultLocale;
}
export function getLangInClient() {
  return Cookies.get(storage_keys.locale)?.slice(0, 2) || i18n.defaultLanguage;
}

export function getDeviceToken() {
  const deviceToken = localStorage.getItem(storage_keys.firebase_device_token);

  return deviceToken;
}

export function checkFireabseBrowserSupport() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    'crypto' in window
  );
}

// We enumerate all dictionaries here for better linting and typescript support
// We also get the default import for cleaner types
const dictionaries = {
  en: () =>
    import('../../dictionaries/en.json').then((module) => module.default),
  de: () =>
    import('../../dictionaries/de.json').then((module) => module.default),
};

export const getClientDictionary = async (lang: string) => {
  // const [lang] = locale.split('-');

  for (const haveLang of i18n.languages) {
    if (haveLang === lang) {
      return dictionaries[lang]?.();
    }
  }

  return dictionaries.de();
};

export const getActiveInactiveStatusKey = (status: boolean) => {
  return status ? 'active' : 'inactive';
};

// Function to filter out values that match given keywords
export const filterObject = (
  filters: any,
  keywords: string[],
  isInclude = true
) => {
  return {
    ...filters,
    type: {
      ...filters.type,
      values: Object.fromEntries(
        Object.entries(filters.type.values).filter(([key]) => {
          if (isInclude) {
            return keywords.some((keyword) =>
              (key as string).toLowerCase().includes(keyword.toLowerCase())
            );
          } else {
            return !keywords.some((keyword) =>
              (key as string).toLowerCase().includes(keyword.toLowerCase())
            );
          }
        })
      ),
    },
  };
};

export const getSearchFiltersByKeys = (
  filtersData: any,
  allowedKeys: string[]
) => {
  const filters =
    allowedKeys?.length > 0 && filtersData
      ? Object.keys(filtersData)
          .filter((key) => allowedKeys.includes(key))
          .reduce((obj: { [key: string]: any }, key) => {
            obj[key] = filtersData[key];
            return obj;
          }, {})
      : filtersData ?? {};

  return filters;
};

export const generateQueryParams = (queryParams: any = {}) => {
  const params = window.location.search.substring(1);

  const urlParams = new URLSearchParams(params);
  let filters: any = {};
  if (urlParams.size > 0) {
    filters = Object.fromEntries(urlParams);
  }

  return {
    ...filters,
    ...queryParams,
  };
};

export const parseJsonWithStatus = async (res: Response) => {
  if (!res.ok) {
    console.error(`HTTP Error: ${res.status} ${res.statusText}`);
    return null;
  }

  try {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await res.json();
    } else {
      console.error('Unexpected content type:', contentType);
      return null;
    }
  } catch (err) {
    console.error('Error parsing JSON:', err);
    return null;
  }
};
